import type { CampaignTemplate } from './cockpit-data';
import {
  TEMPLATE_BIDDING_STRATEGY_OPTIONS,
  TEMPLATE_CAMPAIGN_TYPE_OPTIONS,
  TEMPLATE_MATCH_TYPE_OPTIONS,
  TEMPLATE_PLACEMENT_OPTIONS,
} from './tactic-catalogue';

export type CampaignTemplatesPayload = {
  value: CampaignTemplate[];
  reason: string | null;
  expected_updated_at: string | null;
};

const NAME_PATTERN = /^[A-Z0-9]+(?:-[A-Za-z0-9]+)*$/;
const FUNNEL_STAGE_PATTERN = /^[A-Z0-9]+$/;
const ALLOCATION_FIELDS = ['market', 'target_group', 'asin', 'keyword', 'targeting', 'budget', 'bid'] as const;

export function describeCampaignTemplatesPayload(body: unknown): string | null {
  return normalise(body).error;
}

export function parseCampaignTemplatesPayload(body: unknown): CampaignTemplatesPayload | null {
  const result = normalise(body);
  return result.error ? null : result.payload;
}

function normalise(body: unknown): { payload: CampaignTemplatesPayload | null; error: string | null } {
  if (!isRecord(body)) return invalid('Request body must be an object.');
  if (!Array.isArray(body.value) || body.value.length === 0) {
    return invalid('value must be a non-empty campaign template array.');
  }

  const names = new Set<string>();
  const value: CampaignTemplate[] = [];
  for (let index = 0; index < body.value.length; index += 1) {
    const raw = body.value[index];
    const prefix = `value[${index}]`;
    if (!isRecord(raw)) return invalid(`${prefix} must be an object.`);
    const allocationField = ALLOCATION_FIELDS.find((key) => key in raw);
    if (allocationField) return invalid(`${prefix}.${allocationField} is allocation-bound and is not a template field.`);

    const name = trimmed(raw.name);
    if (!name || name.length > 64 || !NAME_PATTERN.test(name)) {
      return invalid(`${prefix}.name must match ${NAME_PATTERN.source} and be at most 64 characters.`);
    }
    if (names.has(name)) return invalid(`${prefix}.name duplicates ${name}.`);
    names.add(name);

    const campaignType = trimmed(raw.campaign_type);
    if (!TEMPLATE_CAMPAIGN_TYPE_OPTIONS.includes(campaignType as 'SP')) {
      return invalid(`${prefix}.campaign_type must be SP.`);
    }
    const funnelStage = trimmed(raw.funnel_stage);
    if (!funnelStage || !FUNNEL_STAGE_PATTERN.test(funnelStage)) {
      return invalid(`${prefix}.funnel_stage must contain uppercase letters or numbers only.`);
    }
    const matchType = trimmed(raw.match_type);
    if (!TEMPLATE_MATCH_TYPE_OPTIONS.includes(matchType as (typeof TEMPLATE_MATCH_TYPE_OPTIONS)[number])) {
      return invalid(`${prefix}.match_type must be EXACT, PHRASE or BROAD.`);
    }
    const placement = trimmed(raw.placement);
    if (!TEMPLATE_PLACEMENT_OPTIONS.includes(placement as (typeof TEMPLATE_PLACEMENT_OPTIONS)[number])) {
      return invalid(`${prefix}.placement must be ToS or RoS.`);
    }
    const placementPercentage = numeric(raw.placement_percentage);
    if (!Number.isInteger(placementPercentage) || placementPercentage < 0 || placementPercentage > 900) {
      return invalid(`${prefix}.placement_percentage must be an integer from 0 to 900.`);
    }
    const biddingStrategy = trimmed(raw.bidding_strategy);
    if (!TEMPLATE_BIDDING_STRATEGY_OPTIONS.includes(biddingStrategy as (typeof TEMPLATE_BIDDING_STRATEGY_OPTIONS)[number])) {
      return invalid(`${prefix}.bidding_strategy is invalid.`);
    }
    const defaultBudget = numeric(raw.default_budget);
    if (!Number.isFinite(defaultBudget) || defaultBudget <= 0 || defaultBudget > 500) {
      return invalid(`${prefix}.default_budget must be greater than 0 and at most 500.`);
    }
    const siObjective = trimmed(raw.si_objective);
    if (!siObjective || siObjective.length > 120) {
      return invalid(`${prefix}.si_objective must be non-blank and at most 120 characters.`);
    }
    const description = trimmed(raw.description);
    if (description.length > 280) return invalid(`${prefix}.description must be at most 280 characters.`);

    value.push({
      name,
      campaign_type: campaignType,
      funnel_stage: funnelStage,
      match_type: matchType,
      placement,
      placement_percentage: placementPercentage,
      bidding_strategy: biddingStrategy,
      default_budget: Math.round(defaultBudget * 100) / 100,
      si_objective: siObjective,
      ...(raw.retired === true ? { retired: true } : {}),
      ...(description ? { description } : {}),
    });
  }

  if (value.every((template) => template.retired)) {
    return invalid('At least one campaign template must remain active.');
  }
  const reason = rawNullableString(body.reason);
  const expectedUpdatedAt = rawNullableString(body.expected_updated_at);
  if (reason === undefined) return invalid('reason must be a string or null.');
  if (expectedUpdatedAt === undefined) return invalid('expected_updated_at must be a string or null.');
  return {
    payload: { value, reason, expected_updated_at: expectedUpdatedAt },
    error: null,
  };
}

function invalid(error: string): { payload: null; error: string } {
  return { payload: null, error };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function trimmed(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function numeric(value: unknown): number {
  if (typeof value === 'number') return value;
  if (typeof value === 'string' && value.trim()) return Number(value);
  return Number.NaN;
}

function rawNullableString(value: unknown): string | null | undefined {
  if (value === null || value === undefined) return null;
  return typeof value === 'string' ? (value.trim() || null) : undefined;
}
